import { proxy, useSnapshot } from 'valtio'

import { useFeedsInFloor, useFloorState } from '../firebase/hooks/useFloor'
import { useEditingMixState, useMixes } from '../firebase/hooks/useMixes'

const hGap = 0.625
const vGap = (hGap * 16) / 9

const hSlotNOf = (n: number, of: number) => {
  const slotWidth = (100 - (of + 1) * hGap) / of
  return { w: slotWidth, x: hGap + (n - 1) * (slotWidth + hGap) }
}

const vSlotNOf = (n: number, of: number) => {
  const slotHeight = (100 - (of + 1) * vGap) / of
  return { h: slotHeight, y: vGap + (n - 1) * (slotHeight + vGap) }
}

type Position = { x: number; y: number; w: number; h: number }

const layoutState = proxy<{
  layouts: {
    media: Record<number, Record<number | 'media', Position>>
    noMedia: Record<number, Record<number, Position>>
  }
}>({
  layouts: {
    media: {
      0: {
        media: { x: 0, y: 0, w: 100, h: 100 },
      },
      1: {
        media: { x: hGap, y: vGap, w: 85, h: 100 - vGap },
        0: { x: 77.5, y: 50, w: 22.5 - hGap, h: 35 },
      },
      2: {
        media: { x: hGap, y: vGap, w: 85, h: 100 - vGap },
        0: { x: 77.5, y: 10, w: 22.5 - hGap, h: 35 },
        1: { x: 77.5, y: 50, w: 22.5 - hGap, h: 35 },
      },
      3: {
        media: { x: hGap, y: vGap, w: 85, h: 100 - vGap },
        0: { x: 78, y: 8, w: 22 - hGap, h: 29 },
        1: { x: 78, y: 39, w: 22 - hGap, h: 29 },
        2: { x: 78, y: 70, w: 22 - hGap, h: 29 },
      },
      4: {
        media: { x: hGap, y: vGap, w: 85, h: 100 - vGap },
        0: { x: 82, w: 18 - hGap, ...vSlotNOf(1, 4) },
        1: { x: 82, w: 18 - hGap, ...vSlotNOf(2, 4) },
        2: { x: 82, w: 18 - hGap, ...vSlotNOf(3, 4) },
        3: { x: 82, w: 18 - hGap, ...vSlotNOf(4, 4) },
      },
      5: {
        media: { x: hGap, y: vGap, w: 100 - hGap, h: 80 - 2 * vGap },
        0: { y: 80, h: 20 - vGap, ...hSlotNOf(1, 5) },
        1: { y: 80, h: 20 - vGap, ...hSlotNOf(3, 5) },
        2: { y: 80, h: 20 - vGap, ...hSlotNOf(4, 5) },
        3: { y: 80, h: 20 - vGap, ...hSlotNOf(2, 5) },
        4: { y: 80, h: 20 - vGap, ...hSlotNOf(5, 5) },
      },
      6: {
        media: { x: hGap, y: vGap, w: 100 - hGap, h: 80 - 2 * vGap },
        0: { y: 80, h: 20 - vGap, ...hSlotNOf(1, 6) },
        1: { y: 80, h: 20 - vGap, ...hSlotNOf(3, 6) },
        2: { y: 80, h: 20 - vGap, ...hSlotNOf(4, 6) },
        3: { y: 80, h: 20 - vGap, ...hSlotNOf(2, 6) },
        4: { y: 80, h: 20 - vGap, ...hSlotNOf(5, 6) },
        5: { y: 80, h: 20 - vGap, ...hSlotNOf(6, 6) },
      },
    },
    noMedia: {
      0: {},
      1: { 0: { x: 0, y: 0, w: 100, h: 100 } },
      2: {
        0: { y: 17.171875, h: 65.65625, ...hSlotNOf(1, 2) },
        1: { y: 17.171875, h: 65.65625, ...hSlotNOf(2, 2) },
      },
      3: {
        0: { y: 24.32098766, h: 51.35802468, ...hSlotNOf(1, 3) },
        1: { y: 24.32098766, h: 51.35802468, ...hSlotNOf(2, 3) },
        2: { y: 24.32098766, h: 51.35802468, ...hSlotNOf(3, 3) },
      },
      4: {
        0: { ...hSlotNOf(1, 2), ...vSlotNOf(1, 2) },
        1: { ...hSlotNOf(2, 2), ...vSlotNOf(1, 2) },
        2: { ...hSlotNOf(2, 2), ...vSlotNOf(2, 2) },
        3: { ...hSlotNOf(1, 2), ...vSlotNOf(2, 2) },
      },
      5: {
        0: { ...hSlotNOf(1, 3), ...vSlotNOf(1, 2) },
        1: { ...hSlotNOf(2, 3), ...vSlotNOf(1, 2) },
        2: { ...hSlotNOf(2.5, 3), ...vSlotNOf(2, 2) },
        3: { ...hSlotNOf(1.5, 3), ...vSlotNOf(2, 2) },
        4: { ...hSlotNOf(3, 3), ...vSlotNOf(1, 2) },
      },
      6: {
        0: { ...hSlotNOf(1, 3), ...vSlotNOf(1, 2) },
        1: { ...hSlotNOf(2, 3), ...vSlotNOf(1, 2) },
        2: { ...hSlotNOf(2, 3), ...vSlotNOf(2, 2) },
        3: { ...hSlotNOf(1, 3), ...vSlotNOf(2, 2) },
        4: { ...hSlotNOf(3, 3), ...vSlotNOf(1, 2) },
        5: { ...hSlotNOf(3, 3), ...vSlotNOf(2, 2) },
      },
      7: {
        0: { ...hSlotNOf(1, 4), ...vSlotNOf(1, 2) },
        1: { ...hSlotNOf(2, 4), ...vSlotNOf(1, 2) },
        2: { ...hSlotNOf(3, 4), ...vSlotNOf(1, 2) },
        3: { ...hSlotNOf(4, 4), ...vSlotNOf(1, 2) },
        4: { ...hSlotNOf(1.5, 4), ...vSlotNOf(2, 2) },
        5: { ...hSlotNOf(2.5, 4), ...vSlotNOf(2, 2) },
        6: { ...hSlotNOf(3.5, 4), ...vSlotNOf(2, 2) },
      },
      8: {
        0: { ...hSlotNOf(1, 4), ...vSlotNOf(1, 2) },
        1: { ...hSlotNOf(2, 4), ...vSlotNOf(1, 2) },
        2: { ...hSlotNOf(3, 4), ...vSlotNOf(1, 2) },
        3: { ...hSlotNOf(4, 4), ...vSlotNOf(1, 2) },
        4: { ...hSlotNOf(1, 4), ...vSlotNOf(2, 2) },
        5: { ...hSlotNOf(2, 4), ...vSlotNOf(2, 2) },
        6: { ...hSlotNOf(3, 4), ...vSlotNOf(2, 2) },
        7: { ...hSlotNOf(4, 4), ...vSlotNOf(2, 2) },
      },
      9: {
        0: { ...hSlotNOf(1, 3), ...vSlotNOf(1, 3) },
        1: { ...hSlotNOf(2, 3), ...vSlotNOf(1, 3) },
        2: { ...hSlotNOf(3, 3), ...vSlotNOf(1, 3) },
        3: { ...hSlotNOf(1, 3), ...vSlotNOf(2, 3) },
        4: { ...hSlotNOf(2, 3), ...vSlotNOf(2, 3) },
        5: { ...hSlotNOf(3, 3), ...vSlotNOf(2, 3) },
        6: { ...hSlotNOf(1, 3), ...vSlotNOf(3, 3) },
        7: { ...hSlotNOf(2, 3), ...vSlotNOf(3, 3) },
        8: { ...hSlotNOf(3, 3), ...vSlotNOf(3, 3) },
      },
      10: {
        0: { ...hSlotNOf(1.5, 4), ...vSlotNOf(1, 3) },
        1: { ...hSlotNOf(2.5, 4), ...vSlotNOf(1, 3) },
        2: { ...hSlotNOf(3.5, 4), ...vSlotNOf(1, 3) },
        3: { ...hSlotNOf(1, 4), ...vSlotNOf(2, 3) },
        4: { ...hSlotNOf(2, 4), ...vSlotNOf(2, 3) },
        5: { ...hSlotNOf(3, 4), ...vSlotNOf(2, 3) },
        6: { ...hSlotNOf(4, 4), ...vSlotNOf(2, 3) },
        7: { ...hSlotNOf(1.5, 4), ...vSlotNOf(3, 3) },
        8: { ...hSlotNOf(2.5, 4), ...vSlotNOf(3, 3) },
        9: { ...hSlotNOf(3.5, 4), ...vSlotNOf(3, 3) },
      },
      11: {
        0: { ...hSlotNOf(1, 4), ...vSlotNOf(1, 3) },
        1: { ...hSlotNOf(2, 4), ...vSlotNOf(1, 3) },
        2: { ...hSlotNOf(3, 4), ...vSlotNOf(1, 3) },
        3: { ...hSlotNOf(4, 4), ...vSlotNOf(1, 3) },
        4: { ...hSlotNOf(1.5, 4), ...vSlotNOf(2, 3) },
        5: { ...hSlotNOf(2.5, 4), ...vSlotNOf(2, 3) },
        6: { ...hSlotNOf(3.5, 4), ...vSlotNOf(2, 3) },
        7: { ...hSlotNOf(1, 4), ...vSlotNOf(3, 3) },
        8: { ...hSlotNOf(2, 4), ...vSlotNOf(3, 3) },
        9: { ...hSlotNOf(3, 4), ...vSlotNOf(3, 3) },
        10: { ...hSlotNOf(4, 4), ...vSlotNOf(3, 3) },
      },
      12: {
        0: { ...hSlotNOf(1, 4), ...vSlotNOf(1, 3) },
        1: { ...hSlotNOf(2, 4), ...vSlotNOf(1, 3) },
        2: { ...hSlotNOf(3, 4), ...vSlotNOf(1, 3) },
        3: { ...hSlotNOf(4, 4), ...vSlotNOf(1, 3) },
        4: { ...hSlotNOf(1, 4), ...vSlotNOf(2, 3) },
        5: { ...hSlotNOf(2, 4), ...vSlotNOf(2, 3) },
        6: { ...hSlotNOf(3, 4), ...vSlotNOf(2, 3) },
        7: { ...hSlotNOf(4, 4), ...vSlotNOf(2, 3) },
        8: { ...hSlotNOf(1, 4), ...vSlotNOf(3, 3) },
        9: { ...hSlotNOf(2, 4), ...vSlotNOf(3, 3) },
        10: { ...hSlotNOf(3, 4), ...vSlotNOf(3, 3) },
        11: { ...hSlotNOf(4, 4), ...vSlotNOf(3, 3) },
      },
    },
  },
})

const useAutoLayout = (camerasCount: number, hasMedia: boolean) => {
  const { layouts } = useSnapshot(layoutState)

  const layoutType = hasMedia ? 'media' : 'noMedia'
  return layouts[layoutType][camerasCount]
}

const useAutoFeedPosition = (
  type: 'tray' | 'camera' | 'screen' | 'mobile',
  id: number
) => {
  const feedsInFloor = useFeedsInFloor()
  const camerasInFloor = feedsInFloor.filter(
    (f) => f.type === 'camera' || f.type === 'mobile'
  )

  const layout = useAutoLayout(
    camerasInFloor.length,
    feedsInFloor.some((f) => f.type === 'screen' || f.type === 'tray')
  )

  if (type === 'tray') {
    if ('media' in layout && feedsInFloor.find((f) => f.type === 'tray')) {
      return layout.media
    }
    return undefined
  }

  if (type === 'screen') {
    if ('media' in layout && feedsInFloor.find((f) => f.type === 'screen')) {
      return layout.media
    }
    return undefined
  }

  const idx = camerasInFloor.findIndex((f) => f.type === type && f.id === id)

  return layout[idx] as Position | undefined
}

const useCustomFeedPosition = (
  type: 'tray' | 'camera' | 'screen' | 'mobile',
  id: number
) => {
  const mixes = useMixes()

  const floor = useFloorState()

  if (!floor || floor.layout !== 'custom') return undefined

  const mix = mixes.find((m) => m.id === floor.mixId)

  switch (type) {
    case 'tray': {
      const trayFeed = mix?.feeds.find((f) => f.media?.type === 'tray')
      return trayFeed?.position
    }
    case 'screen':
    case 'camera':
    case 'mobile': {
      const feed = mix?.feeds.find(
        (f) => f.media?.type === type && 'id' in f.media && f.media.id === id
      )
      return feed?.position
    }
    default: {
      return undefined
    }
  }
}

const useMixEditingFeedPosition = (
  type: 'tray' | 'camera' | 'screen' | 'mobile',
  id: number
) => {
  const editingMix = useEditingMixState()

  if (!editingMix) return undefined

  const feed = editingMix.feeds.find((f) => {
    if (type === 'tray') {
      return f.media?.type === type
    }
    if (f.media?.type === type) {
      return f.media.id === id
    }
    return false
  })

  return feed?.position
}

const useFeedPosition = (
  type: 'tray' | 'camera' | 'screen' | 'mobile',
  id: number
) => {
  const floor = useFloorState()
  const editingMix = useEditingMixState()
  const editingFeedPosition = useMixEditingFeedPosition(type, id)
  const autoFeedPosition = useAutoFeedPosition(type, id)
  const customFeedPosition = useCustomFeedPosition(type, id)

  if (editingMix) return editingFeedPosition
  if (floor?.layout === 'custom') return customFeedPosition
  return autoFeedPosition
}

const posToStyle = (position: Position | undefined) => {
  if (!position) return { opacity: 0, top: 0, left: 0, height: 0, width: 0 }
  return {
    left: `${position.x}%`,
    top: `${position.y}%`,
    width: `${position.w}%`,
    height: `${position.h}%`,
  }
}

export { layoutState, useFeedPosition, useAutoLayout, posToStyle }
